import { useCallback, useEffect, useState } from "react";
import {
  createOpsObligationApi,
  deleteOpsObligationApi,
  deleteOpsObligationAttachment,
  getOpsObligationApi,
  listOpsObligationsApi,
  patchOpsObligationApi,
  uploadOpsObligationAttachment,
  type OpsObligation,
  type OpsObligationAttachment,
  type OpsObligationLocationChoice,
  type OpsObligationWrite,
} from "@/lib/opsObligationsClient";
import {
  setOpsObligationFocus,
  clearOpsObligationFocus,
} from "@/lib/opsObligations/opsObligationFocusSurface";

export function useOpsObligations(organizationId: string | null) {
  const [rows, setRows] = useState<OpsObligation[]>([]);
  const [locations, setLocations] = useState<OpsObligationLocationChoice[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<OpsObligation | null>(null);
  const [attachments, setAttachments] = useState<OpsObligationAttachment[]>([]);
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [locationId, setLocationId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!organizationId) {
      setRows([]);
      setLocations([]);
      return;
    }
    setLoading(true);
    setError(null);
    const res = await listOpsObligationsApi({
      kind: kind || undefined,
      status: status || undefined,
      location_id: locationId || undefined,
    });
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setRows(res.data.obligations);
    setLocations(res.data.locations);
  }, [organizationId, kind, status, locationId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const loadDetail = useCallback(async (id: string) => {
    setSelectedId(id);
    const res = await getOpsObligationApi(id);
    if (!res.ok) {
      setError(res.error);
      setDetail(null);
      setAttachments([]);
      return;
    }
    setDetail(res.data.obligation);
    setAttachments(res.data.attachments);
    setOpsObligationFocus({ obligation_id: id, kind: res.data.obligation.kind });
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setAttachments([]);
      clearOpsObligationFocus();
      return;
    }
    void loadDetail(selectedId);
    return () => clearOpsObligationFocus();
  }, [selectedId, loadDetail]);

  async function create(input: OpsObligationWrite) {
    const res = await createOpsObligationApi(input);
    if (!res.ok) {
      setError(res.error);
      return null;
    }
    await reload();
    setSelectedId(res.data.obligation.id);
    return res.data.obligation;
  }

  async function save(id: string, patch: OpsObligationWrite) {
    const res = await patchOpsObligationApi(id, patch);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setDetail(res.data.obligation);
    await reload();
  }

  async function remove(id: string) {
    const res = await deleteOpsObligationApi(id);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setSelectedId(null);
    await reload();
  }

  async function addAttachment(file: File) {
    if (!selectedId) return;
    const res = await uploadOpsObligationAttachment(selectedId, file);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    await loadDetail(selectedId);
  }

  async function removeAttachment(attachmentId: string) {
    const res = await deleteOpsObligationAttachment(attachmentId);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    if (selectedId) await loadDetail(selectedId);
  }

  return {
    rows,
    locations,
    selectedId,
    setSelectedId,
    detail,
    attachments,
    kind,
    setKind,
    status,
    setStatus,
    locationId,
    setLocationId,
    loading,
    error,
    reload,
    create,
    save,
    remove,
    addAttachment,
    removeAttachment,
  };
}
